// admin SPA — top-level safety net so a bug in one page (Cert Groups, Service
// Controls, etc.) doesn't kill the whole admin shell. Wraps the route
// outlet; on a render error the user sees a small recovery panel and can
// click another menu item or reload — sidebar + topbar keep working.
//
// Audit fix 2026-05-01.
import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Optional label shown above the message — useful when nesting per page. */
  label?: string;
};

type State = {
  err: Error | null;
  info: ErrorInfo | null;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null, info: null };

  static getDerivedStateFromError(err: Error): Partial<State> {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // Surface the error in the console with the React component stack so the
    // engineer can find the offending page quickly. Don't swallow.
    // eslint-disable-next-line no-console
    console.error('[admin error-boundary]', err, info);
    this.setState({ info });
  }

  reset = () => this.setState({ err: null, info: null });

  render() {
    const { err, info } = this.state;
    if (!err) return this.props.children;
    return (
      <div className="m-6 rounded-lg border border-red-700 bg-red-900/20 p-6 text-ink-text">
        <div className="mb-2 text-lg font-bold text-red-300">
          {this.props.label || 'This page errored'}
        </div>
        <p className="mb-3 text-sm text-ink-muted">
          The admin shell is still working — pick another menu item, or hit Try Again to reload this page.
        </p>
        <details className="mb-4 text-xs">
          <summary className="cursor-pointer text-ink-muted">Show details</summary>
          <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words rounded bg-ink-bg p-3 text-red-200">
            {String(err && (err.stack || err.message || err))}
            {info && info.componentStack ? '\n\n' + info.componentStack : ''}
          </pre>
        </details>
        <div className="flex gap-2">
          <button
            onClick={this.reset}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded bg-ink-hover px-4 py-2 text-sm font-semibold hover:bg-ink-border"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
